import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import {
  MdPeople,
  MdNotifications,
  MdCampaign,
  MdSupport,
  MdSend,
  MdSchedule,
  MdGroups,
  MdTrendingUp,
  MdChat,
  MdAnalytics,
  MdPhoneAndroid,
  MdScience,
  MdAttachMoney,
  MdConfirmationNumber,
  MdCategory,
  MdBadge,
  MdPerson,
  MdViewList,
  MdLink,
  MdEvent,
  MdAddCircle,
} from "react-icons/md";
import {
  Utils,
  fetchWithAuth,
  bigAmountFormat,
  getAuthUserCurrency,
  can,
  isDashboardSectionEnabled,
  getDateRangeForPreset,
  formatDateRangeLabel,
  formatChartAxisLabel,
  getBrowserTimeZone,
  toLocalIsoDate,
  composeEventMetricLabel,
  EVENT_METRIC_OPTION,
} from "../../utils";
import DateRangePicker from "../common/DateRangePicker/DateRangePicker";

const KPI_COLORS = {
  indigo: "#4f46e5",
  green: "#10b981",
  amber: "#f59e0b",
  rose: "#ef4444",
  violet: "#8b5cf6",
  sky: "#0ea5e9",
  teal: "#0d9488",
};

const WA_STATUS_COLORS = {
  sent: "#94a3b8",
  delivered: "#10b981",
  read: "#4f46e5",
  failed: "#ef4444",
  pending: "#f59e0b",
};

function formatChartLabel(period, granularity) {
  return formatChartAxisLabel(period, granularity);
}

function sectionVisible(sectionEnabled, hasPermission, loading, statsSection) {
  return sectionEnabled && hasPermission && (loading || Boolean(statsSection?.visible));
}

function ChartCard({ title, loading, emptyMessage, hasData, children }) {
  return (
    <div className="app-dashboard__chart-card">
      <h3>{title}</h3>
      {loading ? (
        <div className="app-dashboard__chart-skeleton skeleton-cell" aria-hidden="true" />
      ) : hasData ? (
        children
      ) : (
        <p className="app-dashboard__chart-empty">{emptyMessage}</p>
      )}
    </div>
  );
}

function KpiCard({ icon, label, value, displayValue, sub, color, to, loading }) {
  const inner = (
    <>
      <div className="app-dashboard__kpi-icon" style={{ background: `${color}18`, color }}>
        {icon}
      </div>
      <div>
        <div className="app-dashboard__kpi-value">
          {loading ? (
            <div className="skeleton-cell app-dashboard__skeleton" />
          ) : (
            displayValue ?? bigAmountFormat(value ?? 0)
          )}
        </div>
        <div className="app-dashboard__kpi-label">{label}</div>
        {sub && !loading ? <div className="app-dashboard__kpi-sub">{sub}</div> : null}
      </div>
    </>
  );

  if (to && !loading) {
    return (
      <Link to={to} className="app-dashboard__kpi app-dashboard__kpi--link">
        {inner}
      </Link>
    );
  }
  return <div className="app-dashboard__kpi">{inner}</div>;
}

const defaultRange = getDateRangeForPreset("today");

const Dashboard = ({ handleSetAppId }) => {
  const { appId } = useParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [datePreset, setDatePreset] = useState(defaultRange.preset || "today");

  const fetchStats = useCallback(() => {
    setLoading(true);
    fetchWithAuth(Utils.baseurl + "application/dashboard/stats", {
      method: "POST",
      body: JSON.stringify({
        app_id: appId,
        date_from: dateFrom,
        date_to: dateTo,
        timezone: getBrowserTimeZone() || undefined,
        preset: datePreset,
      }),
    })
      .then((response) => response.json())
      .then((result) => {
        if (result.status) setStats(result.data);
      })
      .catch((error) => console.error(error))
      .finally(() => setLoading(false));
  }, [appId, dateFrom, dateTo, datePreset]);

  useEffect(() => {
    handleSetAppId(appId);
  }, [appId, handleSetAppId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleDateRangeChange = ({ dateFrom: from, dateTo: to, preset }) => {
    setDateFrom(from || toLocalIsoDate());
    setDateTo(to || toLocalIsoDate());
    if (preset) setDatePreset(preset);
  };

  const rangeLabel = useMemo(() => {
    if (stats?.date_range?.from && stats?.date_range?.to) {
      return formatDateRangeLabel(stats.date_range.from, stats.date_range.to);
    }
    return formatDateRangeLabel(dateFrom, dateTo);
  }, [stats, dateFrom, dateTo]);

  const chartGranularity = stats?.charts?.granularity || "day";

  const pushChartData = useMemo(() => {
    const rows = stats?.charts?.push_sent_by_day || [];
    return rows.map((r) => ({
      day: formatChartLabel(r.day, chartGranularity),
      Sends: r.sends,
      Recipients: r.recipients,
    }));
  }, [stats, chartGranularity]);

  const waChartData = useMemo(() => {
    const rows = stats?.charts?.whatsapp_status || [];
    return rows.map((r) => ({
      name: r.status ? r.status.charAt(0).toUpperCase() + r.status.slice(1) : "Unknown",
      count: Number(r.count) || 0,
      fill: WA_STATUS_COLORS[r.status] || KPI_COLORS.indigo,
    }));
  }, [stats]);

  const engageChartData = useMemo(() => {
    const rows = stats?.charts?.engage_by_day || [];
    return rows.map((r) => ({
      day: formatChartLabel(r.day, chartGranularity),
      Clicks: r.clicks,
      Installs: r.installs,
      Signups: r.signups,
      Metrics: r.metrics,
    }));
  }, [stats, chartGranularity]);

  const supportChartData = useMemo(() => {
    const rows = stats?.charts?.support_by_day || [];
    return rows.map((r) => ({
      day: formatChartLabel(r.day, chartGranularity),
      New: r.new,
      Open: r.open,
      Closed: r.closed,
    }));
  }, [stats, chartGranularity]);

  const dashboardPerms = useMemo(
    () => ({
      audience:
        can("subscriptions.view") ||
        can("app_users.view") ||
        can("segments.view") ||
        can("test_subscriptions.view"),
      engage: can("campaigns.view") || can("onelink.view") || can("events.view"),
      delivery: can("delivery.sent.view") || can("delivery.scheduled.view"),
      support:
        can("support.tickets.view") ||
        can("support.groups.view") ||
        can("support.team_members.view") ||
        can("support.customers.view") ||
        can("support.fields.view"),
      whatsapp:
        can("whatsapp.analytics.view") ||
        can("whatsapp.inbox.view") ||
        can("whatsapp.campaigns.view"),
      notificationChart: can("delivery.sent.view"),
      engageChart: can("campaigns.view"),
      supportChart: can("support.tickets.view"),
    }),
    []
  );

  const hasAudience = sectionVisible(
    isDashboardSectionEnabled("audience"),
    dashboardPerms.audience,
    loading,
    stats?.audience
  );
  const hasEngage = sectionVisible(
    isDashboardSectionEnabled("engage"),
    dashboardPerms.engage,
    loading,
    stats?.engage
  );
  const hasWhatsapp = sectionVisible(
    isDashboardSectionEnabled("whatsapp"),
    dashboardPerms.whatsapp,
    loading,
    stats?.whatsapp
  );
  const hasDelivery = sectionVisible(
    isDashboardSectionEnabled("delivery"),
    dashboardPerms.delivery,
    loading,
    stats?.delivery
  );
  const hasSupport = sectionVisible(
    isDashboardSectionEnabled("support"),
    dashboardPerms.support,
    loading,
    stats?.support
  );
  const hasAnyKpi = hasAudience || hasEngage || hasWhatsapp || hasDelivery || hasSupport;

  const showNotificationChart =
    isDashboardSectionEnabled("delivery") && dashboardPerms.notificationChart;
  const showWaChart =
    isDashboardSectionEnabled("whatsapp") &&
    can("whatsapp.analytics.view") &&
    !loading &&
    waChartData.length > 0;
  const showEngageChart = isDashboardSectionEnabled("engage") && dashboardPerms.engageChart;
  const showSupportChart = isDashboardSectionEnabled("support") && dashboardPerms.supportChart;
  const hasCharts = showNotificationChart || showWaChart || showEngageChart || showSupportChart;
  const showDateFilter =
    (isDashboardSectionEnabled("engage") && dashboardPerms.engage) ||
    (isDashboardSectionEnabled("delivery") && dashboardPerms.notificationChart) ||
    (isDashboardSectionEnabled("support") && dashboardPerms.supportChart) ||
    showNotificationChart ||
    showEngageChart ||
    showSupportChart;

  return (
    <>
      <div className="dashboard_right_top">
        <div className="dashboard_heading">
          <h2 className="mb-1">Dashboard</h2>
          {stats?.app_name ? (
            <p className="app-dashboard__intro">{stats.app_name} — overview at a glance</p>
          ) : loading ? (
            <p className="app-dashboard__intro">Loading overview…</p>
          ) : null}
        </div>
        <div className="add_app">
          {can("push.view") && (
            <Link to={`/apps/${appId}/push-notification`} className="add_app_btn btn_theme_primary">
              <MdAddCircle />
              <span>New Message</span>
            </Link>
          )}
        </div>
      </div>

      <div className="app-dashboard">
        {showDateFilter && (
          <div className="app-dashboard__filter-bar">
            <div className="app-dashboard__date-range">
              <DateRangePicker
                dateFrom={dateFrom}
                dateTo={dateTo}
                preset={datePreset}
                onChange={handleDateRangeChange}
                label=""
                placeholder="Select date range"
              />
            </div>
          </div>
        )}

        {!loading && !hasAnyKpi && (
          <div className="app-dashboard__empty">
            No dashboard widgets are available for your role. Contact your administrator for access.
          </div>
        )}

        {hasAudience && (
          <section>
            <h3 className="app-dashboard__section-title">Audience</h3>
            <div className="app-dashboard__kpi-grid">
              {can("subscriptions.view") && (loading || stats?.audience?.subscriptions_active != null) && (
                <KpiCard
                  icon={<MdNotifications size={22} />}
                  label="Active subscriptions"
                  value={stats?.audience?.subscriptions_active}
                  sub={
                    !loading && stats?.audience?.subscriptions_total != null
                      ? `${bigAmountFormat(stats.audience.subscriptions_total)} total`
                      : null
                  }
                  color={KPI_COLORS.indigo}
                  to={`/apps/${appId}/subscriptions`}
                  loading={loading}
                />
              )}
              {can("app_users.view") && (loading || stats?.audience?.app_users != null) && (
                <KpiCard
                  icon={<MdPeople size={22} />}
                  label="User records"
                  value={stats?.audience?.app_users}
                  color={KPI_COLORS.green}
                  to={`/apps/${appId}/users`}
                  loading={loading}
                />
              )}
              {can("subscriptions.view") && (loading || stats?.audience?.app_subscribers != null) && (
                <KpiCard
                  icon={<MdPhoneAndroid size={22} />}
                  label="App subscribers (Android / iOS)"
                  value={stats?.audience?.app_subscribers}
                  color={KPI_COLORS.violet}
                  loading={loading}
                />
              )}
              {can("subscriptions.view") && (loading || stats?.audience?.web_push_subscribers != null) && (
                <KpiCard
                  icon={<MdTrendingUp size={22} />}
                  label="Web push subscribers"
                  value={stats?.audience?.web_push_subscribers}
                  color={KPI_COLORS.sky}
                  loading={loading}
                />
              )}
              {can("test_subscriptions.view") && (loading || stats?.audience?.test_subscribers != null) && (
                <KpiCard
                  icon={<MdScience size={22} />}
                  label="Test subscribers"
                  value={stats?.audience?.test_subscribers}
                  color={KPI_COLORS.amber}
                  to={`/apps/${appId}/testsubscription`}
                  loading={loading}
                />
              )}
              {can("subscriptions.view") && (loading || stats?.audience?.mau != null) && (
                <KpiCard
                  icon={<MdAnalytics size={22} />}
                  label="MAU (prev month)"
                  value={stats?.audience?.mau}
                  color={KPI_COLORS.teal}
                  loading={loading}
                />
              )}
              {can("segments.view") && (loading || stats?.audience?.segments_active != null) && (
                <KpiCard
                  icon={<MdGroups size={22} />}
                  label="Active segments"
                  value={stats?.audience?.segments_active}
                  sub={
                    !loading && stats?.audience?.segments_total != null
                      ? `${bigAmountFormat(stats.audience.segments_total)} total`
                      : null
                  }
                  color={KPI_COLORS.amber}
                  to={`/apps/${appId}/segments`}
                  loading={loading}
                />
              )}
            </div>
          </section>
        )}

        {isDashboardSectionEnabled("whatsapp") && hasWhatsapp && (
          <section>
            <h3 className="app-dashboard__section-title">WhatsApp</h3>
            <div className="app-dashboard__kpi-grid">
              {can("whatsapp.analytics.view") && (loading || stats?.whatsapp?.messages_sent_14d != null) && (
                <KpiCard
                  icon={<MdChat size={22} />}
                  label="Messages sent (14 days)"
                  value={stats?.whatsapp?.messages_sent_14d}
                  sub={
                    !loading && stats?.whatsapp?.delivery_rate_pct != null
                      ? `${stats.whatsapp.delivery_rate_pct}% delivered · ${stats.whatsapp.read_rate_pct}% read`
                      : null
                  }
                  color={KPI_COLORS.green}
                  to={`/apps/${appId}/whatsapp/analytics`}
                  loading={loading}
                />
              )}
              {can("whatsapp.inbox.view") && (loading || stats?.whatsapp?.active_conversations != null) && (
                <KpiCard
                  icon={<MdChat size={22} />}
                  label="Conversations"
                  value={stats?.whatsapp?.active_conversations}
                  color={KPI_COLORS.sky}
                  to={`/apps/${appId}/whatsapp/inbox`}
                  loading={loading}
                />
              )}
              {can("whatsapp.campaigns.view") && (loading || stats?.whatsapp?.campaigns_active != null) && (
                <KpiCard
                  icon={<MdCampaign size={22} />}
                  label="Active campaigns"
                  value={stats?.whatsapp?.campaigns_active}
                  color={KPI_COLORS.amber}
                  to={`/apps/${appId}/whatsapp/campaigns`}
                  loading={loading}
                />
              )}
            </div>
          </section>
        )}

        {hasEngage && (
          <section>
            <h3 className="app-dashboard__section-title">Engage ({rangeLabel})</h3>
            <div className="app-dashboard__kpi-grid">
              {can("onelink.view") && (loading || stats?.engage?.onelinks != null) && (
                <KpiCard
                  icon={<MdLink size={22} />}
                  label="OneLinks"
                  value={stats?.engage?.onelinks}
                  color={KPI_COLORS.indigo}
                  to={`/apps/${appId}/engage/onelink`}
                  loading={loading}
                />
              )}
              {can("events.view") && (loading || stats?.engage?.events_total != null) && (
                <KpiCard
                  icon={<MdEvent size={22} />}
                  label="Events"
                  value={stats?.engage?.events_total}
                  color={KPI_COLORS.sky}
                  to={`/apps/${appId}/engage/event`}
                  loading={loading}
                />
              )}
              {can("campaigns.view") && (loading || stats?.engage?.clicks != null) && (
                <KpiCard
                  icon={<MdCampaign size={22} />}
                  label="Link clicks"
                  value={stats?.engage?.clicks}
                  color={KPI_COLORS.indigo}
                  to={`/apps/${appId}/engage/campaign`}
                  loading={loading}
                />
              )}
              {can("campaigns.view") && (loading || stats?.engage?.installs != null) && (
                <KpiCard
                  icon={<MdTrendingUp size={22} />}
                  label="Installs"
                  value={stats?.engage?.installs}
                  color={KPI_COLORS.green}
                  loading={loading}
                />
              )}
              {can("campaigns.view") && (loading || stats?.engage?.signups != null) && (
                <KpiCard
                  icon={<MdPeople size={22} />}
                  label="Signups"
                  value={stats?.engage?.signups}
                  color={KPI_COLORS.violet}
                  loading={loading}
                />
              )}
              {can("campaigns.view") && !loading && (stats?.engage?.metrics || []).map((m) => (
                <React.Fragment key={`metric-${m.id ?? 'default'}`}>
                  {m.show_count !== false && m.count != null && (
                    <KpiCard
                      icon={<MdConfirmationNumber size={22} />}
                      label={composeEventMetricLabel(m.label || EVENT_METRIC_OPTION.label, 'Count')}
                      value={m.count}
                      color={KPI_COLORS.amber}
                      loading={loading}
                    />
                  )}
                  {m.show_amount !== false && m.amount != null && (
                    <KpiCard
                      icon={<MdAttachMoney size={22} />}
                      label={composeEventMetricLabel(m.label || EVENT_METRIC_OPTION.label)}
                      displayValue={bigAmountFormat(m.amount, getAuthUserCurrency())}
                      color={KPI_COLORS.teal}
                      loading={loading}
                    />
                  )}
                </React.Fragment>
              ))}
              {can("campaigns.view") && loading && (
                <>
                  <KpiCard icon={<MdConfirmationNumber size={22} />} label="Events" loading color={KPI_COLORS.amber} />
                  <KpiCard icon={<MdAttachMoney size={22} />} label="Revenue" loading color={KPI_COLORS.teal} />
                </>
              )}
            </div>
          </section>
        )}

        {hasDelivery && (
          <section>
            <h3 className="app-dashboard__section-title">Delivery ({rangeLabel})</h3>
            <div className="app-dashboard__kpi-grid">
              {can("delivery.sent.view") && (loading || stats?.delivery?.sent_in_range != null) && (
                <KpiCard
                  icon={<MdSend size={22} />}
                  label="Push sends"
                  value={stats?.delivery?.sent_in_range}
                  color={KPI_COLORS.indigo}
                  to={`/apps/${appId}/sent-messages`}
                  loading={loading}
                />
              )}
              {can("delivery.scheduled.view") && (loading || stats?.delivery?.scheduled_pending != null) && (
                <KpiCard
                  icon={<MdSchedule size={22} />}
                  label="Scheduled pending"
                  value={stats?.delivery?.scheduled_pending}
                  color={KPI_COLORS.amber}
                  to={`/apps/${appId}/scheduled-messages`}
                  loading={loading}
                />
              )}
            </div>
          </section>
        )}

        {hasSupport && (
          <section>
            <h3 className="app-dashboard__section-title">Customer Support</h3>
            <div className="app-dashboard__kpi-grid">
              {can("support.tickets.view") && (loading || stats?.support?.tickets_new != null) && (
                <KpiCard
                  icon={<MdSupport size={22} />}
                  label="New tickets"
                  value={stats?.support?.tickets_new}
                  color={KPI_COLORS.sky}
                  to={`/apps/${appId}/support/ticket?tab=new`}
                  loading={loading}
                />
              )}
              {can("support.tickets.view") && (loading || stats?.support?.tickets_open != null) && (
                <KpiCard
                  icon={<MdSupport size={22} />}
                  label="Open tickets"
                  value={stats?.support?.tickets_open}
                  color={KPI_COLORS.rose}
                  to={`/apps/${appId}/support/ticket?tab=all_open`}
                  loading={loading}
                />
              )}
              {can("support.tickets.view") && (loading || stats?.support?.tickets_closed != null) && (
                <KpiCard
                  icon={<MdSupport size={22} />}
                  label="Closed tickets"
                  value={stats?.support?.tickets_closed}
                  sub={
                    !loading && stats?.support?.tickets_closed_in_range != null
                      ? `${bigAmountFormat(stats.support.tickets_closed_in_range)} in ${rangeLabel}`
                      : null
                  }
                  color={KPI_COLORS.green}
                  to={`/apps/${appId}/support/ticket?tab=closed`}
                  loading={loading}
                />
              )}
              {can("support.groups.view") && (loading || stats?.support?.groups != null) && (
                <KpiCard
                  icon={<MdCategory size={22} />}
                  label="Groups"
                  value={stats?.support?.groups}
                  color={KPI_COLORS.indigo}
                  to={`/apps/${appId}/support/groups`}
                  loading={loading}
                />
              )}
              {can("support.team_members.view") && (loading || stats?.support?.team_members != null) && (
                <KpiCard
                  icon={<MdBadge size={22} />}
                  label="Team members"
                  value={stats?.support?.team_members}
                  color={KPI_COLORS.violet}
                  to={`/apps/${appId}/support/team-members`}
                  loading={loading}
                />
              )}
              {can("support.customers.view") && (loading || stats?.support?.customers != null) && (
                <KpiCard
                  icon={<MdPerson size={22} />}
                  label="Customers"
                  value={stats?.support?.customers}
                  color={KPI_COLORS.sky}
                  to={`/apps/${appId}/support/customers`}
                  loading={loading}
                />
              )}
              {can("support.fields.view") && (loading || stats?.support?.fields != null) && (
                <KpiCard
                  icon={<MdViewList size={22} />}
                  label="Fields"
                  value={stats?.support?.fields}
                  color={KPI_COLORS.amber}
                  to={`/apps/${appId}/support/fields`}
                  loading={loading}
                />
              )}
            </div>
          </section>
        )}

        {hasCharts && (
          <section className="app-dashboard__charts">
            {showNotificationChart && (
              <ChartCard
                title={`Notifications (${rangeLabel})`}
                loading={loading}
                hasData={pushChartData.length > 0}
                emptyMessage={`No notifications sent in ${rangeLabel}.`}
              >
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={pushChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip formatter={(value) => bigAmountFormat(value)} />
                    <Legend />
                    <Bar dataKey="Sends" fill={KPI_COLORS.indigo} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Recipients" fill={KPI_COLORS.sky} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
            {isDashboardSectionEnabled("whatsapp") && showWaChart && (
              <div className="app-dashboard__chart-card">
                <h3>WhatsApp delivery status (14 days)</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    layout="vertical"
                    data={waChartData}
                    margin={{ top: 4, right: 16, left: 8, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={72} />
                    <Tooltip formatter={(value) => bigAmountFormat(value)} />
                    <Bar dataKey="count" name="Messages" radius={[0, 4, 4, 0]}>
                      {waChartData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {showEngageChart && (
              <ChartCard
                title={`Engage activity (${rangeLabel})`}
                loading={loading}
                hasData={engageChartData.length > 0}
                emptyMessage={`No engage activity in ${rangeLabel}.`}
              >
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={engageChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip formatter={(value) => bigAmountFormat(value)} />
                    <Legend />
                    <Bar dataKey="Clicks" fill={KPI_COLORS.indigo} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Installs" fill={KPI_COLORS.green} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Signups" fill={KPI_COLORS.violet} radius={[4, 4, 0, 0]} />
                    {stats?.engage?.metrics?.[0]?.show_count !== false && (
                      <Bar dataKey="Metrics" name={composeEventMetricLabel(stats?.engage?.metrics?.[0]?.label || EVENT_METRIC_OPTION.label, 'Count')} fill={KPI_COLORS.amber} radius={[4, 4, 0, 0]} />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
            {showSupportChart && (
              <ChartCard
                title={`Support tickets (${rangeLabel})`}
                loading={loading}
                hasData={supportChartData.length > 0}
                emptyMessage={`No support ticket activity in ${rangeLabel}.`}
              >
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={supportChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip formatter={(value) => bigAmountFormat(value)} />
                    <Legend />
                    <Bar dataKey="New" fill={KPI_COLORS.sky} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Open" fill={KPI_COLORS.rose} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Closed" fill={KPI_COLORS.green} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </section>
        )}
      </div>
    </>
  );
};

export default Dashboard;
